<template>
  <div class="sp-list">
    <!-- 查询条件：折叠后只显示标题栏 -->
    <el-card class="search-card" shadow="never">
      <div slot="header" class="search-header">
        <span class="search-title" @click="searchExpanded = !searchExpanded">
          查询条件
          <i :class="searchExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
        </span>
      </div>
      <el-collapse-transition>
        <el-form v-show="searchExpanded" :inline="true" :model="queryForm" size="small" class="search-form" @submit.native.prevent>
          <el-form-item label="科室">
            <department-select v-model="queryForm.department" placeholder="全部科室" clearable style="width: 150px" />
          </el-form-item>
          <el-form-item label="设备名称">
            <el-input v-model="queryForm.device_name" placeholder="设备名称" clearable style="width: 170px" />
          </el-form-item>
          <el-form-item label="报告时间">
            <el-date-picker
              v-model="queryForm.report_time"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              clearable
              style="width: 260px"
            />
          </el-form-item>
          <el-form-item label="有效期至">
            <el-date-picker
              v-model="queryForm.expire_date"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              clearable
              style="width: 260px"
            />
          </el-form-item>
          <el-form-item label="检测结果">
            <el-select v-model="queryForm.conclusion" placeholder="全部" clearable style="width: 130px">
              <el-option label="合格" value="qualified" />
              <el-option label="不合格" value="unqualified" />
            </el-select>
          </el-form-item>
          <el-form-item class="search-buttons">
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh-right" @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>
      </el-collapse-transition>
    </el-card>

    <!-- 表格 -->
    <el-card shadow="never" class="table-card">
      <div class="table-toolbar">
        <el-button type="success" icon="el-icon-plus" @click="openAdd">新增提醒</el-button>
        <el-button
          type="warning"
          icon="el-icon-download"
          :disabled="!selectedRows.length"
          @click="handleBatchExport"
        >批量导出</el-button>
        <el-button
          type="primary"
          icon="el-icon-folder"
          :disabled="!selectedRows.length"
          @click="handleExportCerts"
        >导出资质</el-button>
      </div>
      <!-- 到期汇总：登录进来第一眼就能看到几台过期 / 即将到期 -->
      <expiry-summary
        :expired="stats.expired"
        :soon="stats.soon"
        :total="stats.total"
        unit="个"
      />

      <el-table
        ref="mainTable"
        :data="list"
        row-key="id"
        border
        class="sp-table"
        :row-class-name="rowClassName"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55" align="center" :reserve-selection="true" />
        <el-table-column type="index" :index="mainIndex" label="序号" width="55" align="center" />
        <el-table-column prop="department" label="科室" width="110" />
        <el-table-column prop="device_name" label="设备名称" min-width="150" show-overflow-tooltip />
        <el-table-column prop="model" label="型号" width="120" show-overflow-tooltip />
        <el-table-column prop="factory_no" label="出厂编号" width="130" show-overflow-tooltip />
        <el-table-column label="最大管电压(kV)" width="140" align="center">
          <template slot-scope="scope">{{ (scope.row.max_tube_voltage || []).join(' / ') || '-' }}</template>
        </el-table-column>
        <el-table-column label="最大管电流(mA)" width="140" align="center">
          <template slot-scope="scope">{{ (scope.row.max_tube_current || []).join(' / ') || '-' }}</template>
        </el-table-column>
        <el-table-column prop="report_time" label="报告时间" width="120" align="center" />
        <el-table-column prop="expire_date" label="有效期至" width="120" align="center" />
        <el-table-column prop="detect_cycle" label="检测周期" width="100" align="center" />
        <el-table-column label="检测结果" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.conclusion === 'qualified' ? 'success' : 'danger'" size="medium">
              {{ scope.row.conclusion_display }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="场所报告" width="90" align="center">
          <template slot-scope="scope">
            <a v-if="scope.row.site_report_path" :href="mediaUrl(scope.row.site_report_path)" target="_blank" class="pdf-link">查看</a>
            <span v-else class="pdf-none">未上传</span>
          </template>
        </el-table-column>
        <el-table-column label="性能报告" min-width="140" align="center">
          <template slot-scope="scope">
            <template v-if="scope.row.performance_reports && scope.row.performance_reports.length">
              <a
                v-for="(p, idx) in scope.row.performance_reports"
                :key="idx"
                :href="mediaUrl(p)"
                target="_blank"
                class="pdf-link"
              >报告{{ idx + 1 }}</a>
            </template>
            <span v-else class="pdf-none">未上传</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="140" show-overflow-tooltip />
        <el-table-column label="距到期" width="100" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.days_until_expire !== null"
              :type="scope.row.days_until_expire < 0 ? 'danger' : (scope.row.days_until_expire <= 30 ? 'warning' : 'info')"
              size="medium">
              <template v-if="scope.row.days_until_expire < 0">已过期</template>
              <template v-else-if="scope.row.days_until_expire <= 30">待报检</template>
              <template v-else>{{ scope.row.days_until_expire }}天</template>
            </el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" align="center" fixed="right">
          <template slot-scope="scope">
            <el-tooltip :content="archiveTip(scope.row)" placement="top">
              <el-button type="text" icon="el-icon-time" class="archive-text" @click="openArchiveHistory(scope.row)" @mouseenter.native="onArchiveIconEnter(scope.row)" />
            </el-tooltip>
            <el-button type="text" class="archive-text" @click="openArchive(scope.row)">归档</el-button>
            <el-button type="text" @click="openEdit(scope.row)">编辑</el-button>
            <el-button type="text" class="danger-text" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pager"
        background
        layout="total, sizes, prev, pager, next"
        :total="total"
        :page-size="pageSize"
        :page-sizes="[10, 20, 50]"
        :current-page.sync="currentPage"
        @size-change="handleSizeChange"
        @current-change="fetchList"
      />
    </el-card>

    <!-- 新增 / 编辑弹窗 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="760px" :close-on-click-modal="false" @closed="resetForm">
      <el-form :model="form" :rules="rules" ref="form" label-width="110px" class="sp-form">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="使用科室" prop="department">
              <department-select v-model="form.department" placeholder="使用科室" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="设备名称" prop="device_name">
              <el-input v-model="form.device_name" placeholder="设备名称"
                :class="{ 'ocr-filled': backfilledKeys.includes('device_name') }"
                @input="onOcrFieldEdit('device_name')" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="规格型号">
              <el-input v-model="form.model" placeholder="规格型号"
                :class="{ 'ocr-filled': backfilledKeys.includes('model') }"
                @input="onOcrFieldEdit('model')" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="出厂编号" prop="factory_no">
              <el-input
                v-model="form.factory_no"
                placeholder="出厂编号"
                :class="{ 'ocr-filled': backfilledKeys.includes('factory_no') }"
                @blur="checkFactoryNo"
                @input="onFactoryNoInput"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="最大管电压(1)">
              <el-input v-model="form.max_tube_voltage_1" placeholder="如 150"
                :class="{ 'ocr-filled': backfilledKeys.includes('max_tube_voltage_1') }"
                @input="onOcrFieldEdit('max_tube_voltage_1')">
                <template slot="suffix">kV</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="最大管电流(1)">
              <el-input v-model="form.max_tube_current_1" placeholder="如 1000"
                :class="{ 'ocr-filled': backfilledKeys.includes('max_tube_current_1') }"
                @input="onOcrFieldEdit('max_tube_current_1')">
                <template slot="suffix">mA</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20" v-if="showSecondVoltGroup">
          <el-col :span="12">
            <el-form-item label="最大管电压(2)" prop="max_tube_voltage_2" :required="showSecondVoltGroup">
              <el-input v-model="form.max_tube_voltage_2" placeholder="如 125"
                :class="{ 'ocr-filled': backfilledKeys.includes('max_tube_voltage_2') }"
                @input="onOcrFieldEdit('max_tube_voltage_2')">
                <template slot="suffix">kV</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="最大管电流(2)" prop="max_tube_current_2" :required="showSecondVoltGroup">
              <el-input v-model="form.max_tube_current_2" placeholder="如 40"
                :class="{ 'ocr-filled': backfilledKeys.includes('max_tube_current_2') }"
                @input="onOcrFieldEdit('max_tube_current_2')">
                <template slot="suffix">mA</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="报告时间" prop="report_time">
              <el-date-picker v-model="form.report_time" type="date" value-format="yyyy-MM-dd" placeholder="请选择报告时间" style="width: 100%"
                :class="{ 'ocr-filled': backfilledKeys.includes('report_time') }"
                @change="onOcrFieldEdit('report_time')" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="有效期至">
              <el-date-picker v-model="form.expire_date" type="date" value-format="yyyy-MM-dd" placeholder="自动计算" style="width: 100%" :disabled="true" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="检测周期">
              <el-input v-model="form.detect_cycle" placeholder="请输入月份，如 12"
                :class="{ 'ocr-filled': backfilledKeys.includes('detect_cycle') }"
                @input="onOcrFieldEdit('detect_cycle')">
                <template slot="suffix">月</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="检测结果">
              <el-select v-model="form.conclusion" placeholder="请选择" style="width: 100%"
                :class="{ 'ocr-filled': backfilledKeys.includes('conclusion') }"
                @change="onOcrFieldEdit('conclusion')">
                <el-option label="合格" value="qualified" />
                <el-option label="不合格" value="unqualified" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="备注">
          <el-input v-model="form.remark" type="textarea" :rows="2" placeholder="备注" />
        </el-form-item>
        <el-form-item label="场所报告">
          <div v-if="form.site_report_path && !siteFile" class="pdf-existing">
            <a :href="mediaUrl(form.site_report_path)" target="_blank">查看当前文件</a>
          </div>
          <el-upload
            ref="siteUpload"
            :auto-upload="false"
            :limit="1"
            accept=".pdf"
            drag
            :file-list="siteFileList"
            :on-change="handleSiteChange"
            :on-remove="handleSiteRemove"
            class="pdf-uploader"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将场所报告 PDF 拖到此处，或<em>点击上传</em>（单个文件）</div>
          </el-upload>
        </el-form-item>
        <el-form-item label="性能报告">
          <div v-if="form.performance_reports && form.performance_reports.length && !perfFiles.length" class="pdf-existing">
            <a
              v-for="(p, idx) in form.performance_reports"
              :key="idx"
              :href="mediaUrl(p)"
              target="_blank"
              class="pdf-link"
            >报告{{ idx + 1 }}</a>
          </div>
          <el-upload
            ref="perfUpload"
            :auto-upload="false"
            multiple
            accept=".pdf"
            drag
            :file-list="perfFileList"
            :on-change="handlePerfChange"
            :on-remove="handlePerfRemove"
            class="pdf-uploader"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将性能报告 PDF 拖到此处，或<em>点击上传</em>（可多个）</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-checkbox v-if="ocrFilled" v-model="ocrConfirmed" class="ocr-confirm-check">
          已核对识别结果（{{ backfilledKeys.length }} 项）无误
        </el-checkbox>
        <el-button type="warning" icon="el-icon-magic-stick" :loading="extracting"
                   :disabled="perfFiles.length === 0" @click="handleExtract">识别报告</el-button>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">保存</el-button>
      </span>
    </el-dialog>

    <!-- 归档（重新检测）弹窗 -->
    <el-dialog title="场所性能归档（重新检测）" :visible.sync="archiveDialogVisible" width="760px" :close-on-click-modal="false" @closed="resetArchiveForm">
      <el-alert
        type="warning"
        :closable="false"
        show-icon
        title="归档后将把旧的场所/性能报告移入归档文件夹（按 科室~设备名称~出厂编号~报告时间 命名），并以新的报告时间更新本记录、存入新报告。记录将保留在列表中。"
      />
      <el-form :model="archiveForm" :rules="archiveRules" ref="archiveForm" label-width="110px" class="sp-form archive-form">
        <el-row :gutter="20" style="margin-top: 12px">
          <el-col :span="12">
            <el-form-item label="使用科室">
              <el-input :value="archiveForm.department" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="设备名称">
              <el-input :value="archiveForm.device_name" disabled />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="出厂编号">
              <el-input :value="archiveForm.factory_no" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="检测周期">
              <el-input v-model="archiveForm.detect_cycle" placeholder="请输入月份，如 12">
                <template slot="suffix">月</template>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="报告时间" prop="report_time">
              <el-date-picker v-model="archiveForm.report_time" type="date" value-format="yyyy-MM-dd" placeholder="请选择报告时间" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="有效期至">
              <el-date-picker v-model="archiveForm.expire_date" type="date" value-format="yyyy-MM-dd" placeholder="自动计算" style="width: 100%" :disabled="true" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="新场所报告" prop="site_file">
          <el-upload
            ref="archiveSiteUpload"
            :auto-upload="false"
            :limit="1"
            accept=".pdf"
            drag
            :file-list="archiveSiteFileList"
            :on-change="handleArchiveSiteChange"
            :on-remove="handleArchiveSiteRemove"
            class="pdf-uploader"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将新的场所报告 PDF 拖到此处，或<em>点击上传</em>（单个文件）</div>
          </el-upload>
        </el-form-item>
        <el-form-item label="新性能报告" prop="performance_file">
          <el-upload
            ref="archivePerfUpload"
            :auto-upload="false"
            multiple
            accept=".pdf"
            drag
            :file-list="archivePerfFileList"
            :on-change="handleArchivePerfChange"
            :on-remove="handleArchivePerfRemove"
            class="pdf-uploader"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将新的性能报告 PDF 拖到此处，或<em>点击上传</em>（可多个）</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="archiveDialogVisible = false">取消</el-button>
        <el-button type="warning" icon="el-icon-folder-checked" @click="submitArchive">确认归档</el-button>
      </span>
    </el-dialog>

    <!-- 归档记录查看弹窗 -->
    <el-dialog title="场所性能归档记录" :visible.sync="archiveHistoryVisible" width="760px" :close-on-click-modal="false">
      <el-alert
        v-if="archiveRow"
        type="info"
        :closable="false"
        show-icon
        :title="`「${archiveRow.department || '-'} / ${archiveRow.device_name}」的历史归档文件（旧报告按归档批次存放于「归档」文件夹，按 科室~设备名称~出厂编号~报告时间 命名）`"
      />
      <el-table
        v-loading="archiveHistoryLoading"
        :data="archiveFiles"
        border
        size="small"
        empty-text="暂无归档记录"
        style="margin-top: 12px"
      >
        <el-table-column type="index" label="序号" width="55" align="center" />
        <el-table-column prop="year" label="年份" width="90" align="center" />
        <el-table-column label="类型" width="80" align="center">
          <template slot-scope="scope">{{ scope.row.kind || '-' }}</template>
        </el-table-column>
        <el-table-column prop="name" label="归档文件名" min-width="260" show-overflow-tooltip />
        <el-table-column label="大小" width="90" align="center">
          <template slot-scope="scope">{{ formatSize(scope.row.size) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="80" align="center">
          <template slot-scope="scope">
            <a :href="scope.row.url" target="_blank" class="pdf-link">查看</a>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer">
        <el-button @click="archiveHistoryVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 导出资质：年份选择弹窗 -->
    <el-dialog title="选择导出年份" :visible.sync="exportCertVisible" width="420px" :close-on-click-modal="false">
      <div class="export-year-tip">以下年份存在已选记录的报告（含当前报告与已归档报告），请勾选需要导出的年份（默认全选）：</div>
      <el-checkbox-group v-model="exportCertYears" class="export-year-group">
        <el-checkbox v-for="y in exportCertYearOptions" :key="y" :label="y">{{ y }} 年</el-checkbox>
      </el-checkbox-group>
      <span slot="footer">
        <el-button @click="exportCertVisible = false">取消</el-button>
        <el-button type="primary" icon="el-icon-download" :loading="exporting" @click="confirmExportCerts">确认导出</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import api from '../api/sitePerformance'

import DepartmentSelect from './DepartmentSelect.vue'
import ExpirySummary from './ExpirySummary.vue'

export default {
  name: 'SitePerformanceList',
  components: { DepartmentSelect, ExpirySummary },
  data() {
    return {
      // 顶部汇总：已过期 / 30 天内到期 / 合计（跟随当前筛选，后端 stats 接口给）
      stats: { expired: 0, soon: 0, total: 0 },
      list: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      selectedRows: [],
      ordering: '',
      searchExpanded: false,
      queryForm: {
        department: '',
        device_name: '',
        report_time: ['', ''],
        expire_date: ['', ''],
        conclusion: '',
      },
      dialogVisible: false,
      dialogTitle: '新增场所性能提醒',
      editingId: null,
      // 上传文件（场所单 / 性能多）
      siteFile: null,
      siteFileList: [],
      perfFiles: [],
      perfFileList: [],
      // 识别报告回填状态
      extracting: false,
      backfilledKeys: [],
      ocrFilled: false,    // 是否已回填过字段（控制确认复选框与提示显隐）
      ocrConfirmed: false, // 用户是否已勾选「确认无误」
      _applyingOcr: false, // 程序回填中标记，避免回填过程误触发确认重置
      // 出厂编号失焦查重标记
      factoryNoExists: false,
      checkingFactoryNo: false,
      form: this.emptyForm(),
      rules: {
        device_name: [{ required: true, message: '请输入设备名称', trigger: 'blur' }],
        factory_no: [
          { required: true, message: '请输入出厂编号', trigger: 'blur' },
          { validator: this.validateFactoryNo, trigger: 'blur' },
        ],
        report_time: [{ required: true, message: '请选择报告时间', trigger: 'change' }],
        // 第二组管电压/管电流：仅当上传第二个性能报告（第二组输入框出现）时必填
        max_tube_voltage_2: [
          {
            validator: (rule, value, callback) => {
              if (this.showSecondVoltGroup && (value === '' || value == null)) {
                callback(new Error('请输入第二组最大管电压'))
              } else {
                callback()
              }
            },
            trigger: 'blur',
          },
        ],
        max_tube_current_2: [
          {
            validator: (rule, value, callback) => {
              if (this.showSecondVoltGroup && (value === '' || value == null)) {
                callback(new Error('请输入第二组最大管电流'))
              } else {
                callback()
              }
            },
            trigger: 'blur',
          },
        ],
      },
      // 归档弹窗
      archiveDialogVisible: false,
      archiveId: null,
      archiveRow: {},
      archiveSiteFile: null,
      archiveSiteFileList: [],
      archivePerfFiles: [],
      archivePerfFileList: [],
      // 归档记录查看弹窗
      archiveHistoryVisible: false,
      archiveHistoryLoading: false,
      archiveFiles: [],
      archiveRow: null,
      archiveCounts: {},
      archiveForm: {
        department: '',
        device_name: '',
        factory_no: '',
        report_time: '',
        expire_date: '',
        detect_cycle: '',
      },
      archiveRules: {
        report_time: [{ required: true, message: '请选择报告时间', trigger: 'change' }],
      },
      // 导出资质弹窗状态
      exportCertVisible: false,
      exportCertYears: [],
      exportCertYearOptions: [],
      exporting: false,
    }
  },
  mounted() {
    this.fetchList()
  },
  watch: {
    'form.report_time': 'calcExpireDate',
    'form.detect_cycle': 'calcExpireDate',
    'archiveForm.report_time': 'calcArchiveExpireDate',
    'archiveForm.detect_cycle': 'calcArchiveExpireDate',
    // 表单任意字段变化（深度监听）：若已回填且非程序回填，则取消「确认无误」勾选
    form: {
      handler() {
        if (this.ocrFilled && !this._applyingOcr) {
          this.ocrConfirmed = false
        }
      },
      deep: true,
    },
  },
  computed: {
    // 当性能报告达到 2 个（上传中或已存在）时，显示第二组管电压/管电流输入框
    showSecondVoltGroup() {
      const perfCount =
        this.perfFiles.length ||
        (this.form.performance_reports && this.form.performance_reports.length) ||
        0
      return perfCount >= 2
    },
  },
  methods: {
    emptyForm() {
      return {
        department: '',
        device_name: '',
        model: '',
        factory_no: '',
        max_tube_voltage_1: '',
        max_tube_current_1: '',
        max_tube_voltage_2: '',
        max_tube_current_2: '',
        report_time: '',
        expire_date: '',
        detect_cycle: '',
        conclusion: '',
        remark: '',
        site_report_path: '',
        performance_reports: [],
      }
    },
    mediaUrl(path) {
      if (!path) return ''
      return '/media/' + path
    },
    // 报告时间 + 检测周期(月) = 有效期至，处理月末溢出
    calcExpireDate() {
      const dateStr = this.form.report_time
      const months = parseInt(this.form.detect_cycle, 10)
      if (!dateStr || isNaN(months) || months <= 0) return
      const parts = dateStr.split('-')
      if (parts.length !== 3) return
      const y = parseInt(parts[0], 10)
      const m = parseInt(parts[1], 10)
      const d = parseInt(parts[2], 10)
      if (isNaN(y) || isNaN(m) || isNaN(d)) return
      const totalMonths = y * 12 + (m - 1) + months
      const newY = Math.floor(totalMonths / 12)
      const newM = (totalMonths % 12) + 1
      const lastDay = new Date(newY, newM, 0).getDate()
      const newD = Math.min(d, lastDay)
      this.form.expire_date = `${newY}-${String(newM).padStart(2, '0')}-${String(newD).padStart(2, '0')}`
    },
    calcArchiveExpireDate() {
      const dateStr = this.archiveForm.report_time
      const months = parseInt(this.archiveForm.detect_cycle, 10)
      if (!dateStr || isNaN(months) || months <= 0) return
      const parts = dateStr.split('-')
      if (parts.length !== 3) return
      const y = parseInt(parts[0], 10)
      const m = parseInt(parts[1], 10)
      const d = parseInt(parts[2], 10)
      if (isNaN(y) || isNaN(m) || isNaN(d)) return
      const totalMonths = y * 12 + (m - 1) + months
      const newY = Math.floor(totalMonths / 12)
      const newM = (totalMonths % 12) + 1
      const lastDay = new Date(newY, newM, 0).getDate()
      const newD = Math.min(d, lastDay)
      this.archiveForm.expire_date = `${newY}-${String(newM).padStart(2, '0')}-${String(newD).padStart(2, '0')}`
    },
    buildParams() {
      const p = { page: this.currentPage, page_size: this.pageSize }
      const q = this.queryForm
      if (q.department) p.department = q.department
      if (q.device_name) p.device_name = q.device_name
      // 报告时间 / 有效期至 改为时间范围查询
      if (q.report_time && q.report_time.length === 2 && q.report_time[0] && q.report_time[1]) {
        p.report_time_start = q.report_time[0]
        p.report_time_end = q.report_time[1]
      }
      if (q.expire_date && q.expire_date.length === 2 && q.expire_date[0] && q.expire_date[1]) {
        p.expire_date_start = q.expire_date[0]
        p.expire_date_end = q.expire_date[1]
      }
      if (q.conclusion) p.conclusion = q.conclusion
      if (this.ordering) p.ordering = this.ordering
      return p
    },
    // 顶部汇总：不带分页参数，只要筛选条件
    async fetchStats() {
      try {
        const p = this.buildParams()
        delete p.page
        delete p.page_size
        const res = await api.stats(p)
        const d = res.data || {}
        this.stats = {
          expired: d.expired || 0,
          soon: d.soon || 0,
          total: d.total || 0,
        }
      } catch (e) {
        // 汇总失败静默保持上次值，不打扰列表使用
      }
    },
    async fetchList() {
      try {
        const res = await api.list(this.buildParams())
        const data = res.data
        this.list = data.results || data
        this.total = data.count || (Array.isArray(data) ? data.length : 0)
        // 汇总与列表同源筛选，列表刷新后顺手刷新（失败不影响列表）
        this.fetchStats()
        // 跨页勾选由 row-key + reserve-selection 接管，不再手工清洗选中项
      } catch (e) {
        // 错误已由拦截器提示
      }
    },
    handleSearch() {
      this.currentPage = 1
      this.fetchList()
    },
    // 切换每页条数：回到第 1 页，防止按旧的深页码请求越界
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
      this.fetchList()
    },
    handleReset() {
      this.queryForm = {
        department: '',
        device_name: '',
        report_time: ['', ''],
        expire_date: ['', ''],
        conclusion: '',
      }
      this.ordering = ''
      this.currentPage = 1
      this.fetchList()
    },
    handleSelectionChange(rows) {
      this.selectedRows = rows || []
    },
    // 到期行高亮：已过期=红、30 天内待报检=橙
    // ⚠️ 用 row-class-name + td 样式，不要用 row-style：
    // row-style 写在 <tr> 上，会被 App.vue 暖米白主题给 td 刷的不透明底色盖掉。
    rowClassName({ row }) {
      const d = row.days_until_expire
      if (d == null) return ''
      if (d < 0) return 'expired-row'
      if (d <= 30) return 'soon-row'
      return ''
    },
    // ---- 上传处理 ----
    handleSiteChange(file, fileList) {
      this.siteFile = file.raw
      this.siteFileList = fileList.slice(-1)
    },
    handleSiteRemove() {
      this.siteFile = null
      this.siteFileList = []
    },
    handlePerfChange(file, fileList) {
      this.perfFileList = fileList
      this.perfFiles = fileList.map(f => f.raw)
    },
    handlePerfRemove(file, fileList) {
      this.perfFileList = fileList
      this.perfFiles = fileList.map(f => f.raw)
    },
    // ---- 新增 / 编辑 ----
    openAdd() {
      this.dialogTitle = '新增场所性能提醒'
      this.editingId = null
      this.form = this.emptyForm()
      this.siteFile = null
      this.siteFileList = []
      this.perfFiles = []
      this.perfFileList = []
      this.factoryNoExists = false
      this.dialogVisible = true
    },
    openEdit(row) {
      this.dialogTitle = '编辑场所性能提醒'
      this.editingId = row.id
      this.form = this.emptyForm()
      this.form.department = row.department || ''
      this.form.device_name = row.device_name || ''
      this.form.model = row.model || ''
      this.form.factory_no = row.factory_no || ''
      const v = Array.isArray(row.max_tube_voltage) ? row.max_tube_voltage : []
      const c = Array.isArray(row.max_tube_current) ? row.max_tube_current : []
      this.form.max_tube_voltage_1 = v[0] != null ? String(v[0]) : ''
      this.form.max_tube_current_1 = c[0] != null ? String(c[0]) : ''
      this.form.max_tube_voltage_2 = v[1] != null ? String(v[1]) : ''
      this.form.max_tube_current_2 = c[1] != null ? String(c[1]) : ''
      this.form.report_time = row.report_time || ''
      this.form.expire_date = row.expire_date || ''
      this.form.detect_cycle = row.detect_cycle || ''
      this.form.conclusion = row.conclusion || ''
      this.form.remark = row.remark || ''
      this.form.site_report_path = row.site_report_path || ''
      this.form.performance_reports = row.performance_reports || []
      this.siteFile = null
      this.siteFileList = []
      this.perfFiles = []
      this.perfFileList = []
      this.factoryNoExists = false
      this.dialogVisible = true
    },
    // 出厂编号失焦查重：调用列表接口按 factory_no 精确过滤，命中（排除自身）即提示重复
    async checkFactoryNo() {
      const val = (this.form.factory_no || '').trim()
      this.factoryNoExists = false
      if (!val) return
      this.checkingFactoryNo = true
      try {
        const res = await api.list({ factory_no: val, page: 1, page_size: 20 })
        const results = (res.data && res.data.results) || []
        // 编辑时排除当前记录自身，避免把自己当成重复
        const exists = results.some(r => r.factory_no === val && r.id !== this.editingId)
        this.factoryNoExists = exists
        if (exists) {
          // 重新触发本字段校验，渲染错误提示
          this.$refs.form.validateField('factory_no')
        }
      } catch (e) {
        // 查重失败（如后端未启动）不阻断填写，仅忽略
        this.factoryNoExists = false
      } finally {
        this.checkingFactoryNo = false
      }
    },
    // 出厂编号输入变化时重置查重标记，避免旧提示残留；同时取消识别回填黄底
    onFactoryNoInput() {
      this.factoryNoExists = false
      this.onOcrFieldEdit('factory_no')
    },
    // 接入 el-form 校验：查重命中则报“重复”，否则放行
    validateFactoryNo(rule, value, callback) {
      if (this.factoryNoExists) {
        callback(new Error('出厂编号重复，请重新输入'))
      } else {
        callback()
      }
    },
    // ---- 识别报告回填（最大管电压 / 最大管电流）----
    // 调后端识别单个 PDF，返回 data 对象（max_tube_voltage / max_tube_current）
    async extractOne(file) {
      // 同一份文件在一次识别里可能被请求多次（兜底/第 2 组对比时），
      // 按文件指纹缓存识别结果，避免对同一 PDF 重复 OCR（一次 OCR 要几十秒）
      if (!this._extractCache) this._extractCache = {}
      const key = [file.name, file.size, file.lastModified].join('_')
      if (this._extractCache[key]) return this._extractCache[key]
      const fd = new FormData()
      fd.append('pdf_file', file)
      const res = await api.extractPdf(fd)
      const d = (res.data && res.data.data) || {}
      this._extractCache[key] = d
      return d
    },
    async handleExtract() {
      // 只识别性能报告（场所报告不再参与识别）：
      //   第 1 份性能报告 -> 通用字段（设备名称/型号/出厂编号/报告时间/周期/结论）
      //                     + 第 1 组管电压/管电流；
      //   有第 2 份时 -> 第 2 份填第 2 组管电压/管电流。
      // 注：两份串行识别——实测并发无收益（OCR 已吃满 CPU，两份并行只是互相抢核，
      // 总耗时不变），串行更简单、峰值内存更低。
      const perfs = this.perfFiles || []
      if (!perfs.length) {
        this.$message.warning('请先上传性能报告')
        return
      }
      this.extracting = true
      this._applyingOcr = true
      try {
        const fillKeys = ['device_name', 'model', 'factory_no', 'report_time', 'detect_cycle', 'conclusion']
        const filled = []
        // 1) 第 1 份性能报告：通用字段 + 第 1 组管电压/管电流
        const d = await this.extractOne(perfs[0])
        fillKeys.forEach(key => {
          const v = d[key]
          if (v !== undefined && v !== null && v !== '') {
            this.form[key] = String(v)
            filled.push(key)
          }
        })
        if (d.max_tube_voltage) {
          this.form.max_tube_voltage_1 = String(d.max_tube_voltage)
          filled.push('max_tube_voltage_1')
        }
        if (d.max_tube_current) {
          this.form.max_tube_current_1 = String(d.max_tube_current)
          filled.push('max_tube_current_1')
        }
        // 2) 第 2 份性能报告 -> 第 2 组管电压/管电流
        if (perfs.length >= 2) {
          const p2 = await this.extractOne(perfs[1])
          if (p2.max_tube_voltage) {
            this.form.max_tube_voltage_2 = String(p2.max_tube_voltage)
            filled.push('max_tube_voltage_2')
          }
          if (p2.max_tube_current) {
            this.form.max_tube_current_2 = String(p2.max_tube_current)
            filled.push('max_tube_current_2')
          }
        }
        this.backfilledKeys = filled
        this.ocrFilled = filled.length > 0
        this.ocrConfirmed = false
        // 出厂编号被回填后显式查重（回填不触发 blur），命中会在编号下方显示“请归档”提示
        if (filled.includes('factory_no')) this.checkFactoryNo()
        this.$message.success(`已回填 ${filled.length} 项，黄色标注部分请对照原报告核对`)
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.detail) || '识别失败，请检查后端服务'
        this.$message.error(msg)
      } finally {
        this.extracting = false
        this._applyingOcr = false
      }
    },
    // 用户手动编辑被识别回填的字段后，取消其黄色高亮、并要求重新勾选确认
    onOcrFieldEdit(key) {
      if (this._applyingOcr) return // 程序回填过程不处理
      const i = this.backfilledKeys.indexOf(key)
      if (i !== -1) {
        this.backfilledKeys.splice(i, 1)
        this.ocrConfirmed = false // 改动了回填字段，需重新核对确认
      }
    },
    buildFormData() {
      const fd = new FormData()
      const fields = [
        'department', 'device_name', 'model', 'factory_no',
        'report_time',
        'detect_cycle', 'expire_date', 'conclusion', 'remark',
      ]
      fields.forEach(f => {
        const v = this.form[f]
        if (v !== null && v !== undefined && v !== '') fd.append(f, v)
      })
      // 最大管电压 / 管电流：按组组装为数组（最多 2 组，数组下标对应性能报告）
      const voltages = []
      const currents = []
      if (this.form.max_tube_voltage_1 !== '' && this.form.max_tube_voltage_1 != null) {
        voltages.push(this.form.max_tube_voltage_1)
      }
      if (this.form.max_tube_current_1 !== '' && this.form.max_tube_current_1 != null) {
        currents.push(this.form.max_tube_current_1)
      }
      if (this.showSecondVoltGroup) {
        if (this.form.max_tube_voltage_2 !== '' && this.form.max_tube_voltage_2 != null) {
          voltages.push(this.form.max_tube_voltage_2)
        }
        if (this.form.max_tube_current_2 !== '' && this.form.max_tube_current_2 != null) {
          currents.push(this.form.max_tube_current_2)
        }
      }
      if (voltages.length) fd.append('max_tube_voltage', JSON.stringify(voltages))
      if (currents.length) fd.append('max_tube_current', JSON.stringify(currents))
      if (this.siteFile) fd.append('site_file', this.siteFile)
      if (this.perfFiles.length) {
        this.perfFiles.forEach(f => fd.append('performance_file', f))
      }
      return fd
    },
    async submitForm() {
      // OCR 回填后必须先勾选「已核对识别结果」方可保存（与计量提醒模块一致）
      if (this.ocrFilled && !this.ocrConfirmed) {
        this.$message.warning('请先勾选「已核对识别结果无误」再保存')
        return
      }
      this.$refs.form.validate(async valid => {
        if (!valid) return
        try {
          const fd = this.buildFormData()
          if (this.editingId) {
            await api.update(this.editingId, fd)
            this.$message.success('保存成功')
          } else {
            await api.create(fd)
            this.$message.success('新增成功')
          }
          this.dialogVisible = false
          this.fetchList()
        } catch (e) {
          const data = e.response && e.response.data
          let msg = '保存失败，请检查后端服务'
          if (data) {
            if (typeof data === 'string') msg = data
            else if (data.factory_no) msg = data.factory_no[0]
            else if (data.detail) msg = data.detail
            else if (data.non_field_errors) msg = data.non_field_errors[0]
          }
          this.$message.error(msg)
        }
      })
    },
    // 主列表序号：跨页连续编号（第 2 页从 11 开始，不重新起 1）
    mainIndex(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1
    },
    // 拉取与当前筛选/排序一致的全量列表，建立 id→序号 映射（序号=在全量结果中的位置，与页面显示一致）
    // 后端 max_page_size=100，超过 100 条时循环翻页补齐
    async fetchSeqMap() {
      const map = {}
      try {
        const pageSize = 100
        let page = 1
        let total = Infinity
        while ((page - 1) * pageSize < total) {
          const params = { ...this.buildParams(), page, page_size: pageSize }
          const res = await api.list(params)
          const data = res.data
          const rows = data.results || data
          rows.forEach((row, i) => { map[row.id] = (page - 1) * pageSize + i + 1 })
          total = data.count || rows.length
          if (!rows.length) break
          page++
        }
      } catch (e) {
        // 拉取失败返回空映射，序号留空但不阻塞导出
      }
      return map
    },
    // ---- 批量导出 ----
    async handleBatchExport() {
      if (!this.selectedRows.length) {
        this.$message.warning('请先在列表中至少选择一条需要导出的记录')
        return
      }
      const seqMap = await this.fetchSeqMap()
      const headers = {
        seq: '序号',
        department: '科室',
        device_name: '设备名称',
        model: '型号',
        factory_no: '出厂编号',
        max_tube_voltage: '最大管电压(kV)',
        max_tube_current: '最大管电流(mA)',
        report_time: '报告时间',
        expire_date: '有效期至',
        detect_cycle: '检测周期',
        conclusion_display: '检测结果',
        site_report_path: '场所报告路径',
        performance_report_paths: '性能报告路径',
        remark: '备注',
        days_until_expire: '距到期天数',
        created_at: '创建时间',
        updated_at: '更新时间',
      }
      const exportData = this.selectedRows.map(row => {
        const item = {}
        Object.keys(headers).forEach(key => {
          let value = row[key]
          if (key === 'seq') value = seqMap[row.id] || ''
          if (Array.isArray(value)) value = value.join(' / ')
          if (key === 'performance_report_paths' && Array.isArray(row.performance_reports)) {
            value = row.performance_reports.map(p => p.split('/').pop()).join('; ')
          }
          item[headers[key]] = value !== undefined && value !== null ? value : ''
        })
        return item
      })
      const ws = XLSX.utils.json_to_sheet(exportData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '场所性能提醒')
      const count = this.selectedRows.length
      const firstName = this.selectedRows[0].device_name || '导出'
      const fileName = count === 1
        ? `场所性能提醒_${firstName}_${this.formatDateForFile(new Date())}.xlsx`
        : `场所性能提醒_批量${count}条_${this.formatDateForFile(new Date())}.xlsx`
      XLSX.writeFile(wb, fileName)
      this.$message.success(`已成功导出 ${count} 条记录`)
    },
    // 导出资质：向后端查询所选记录可导出报告的年份（含当前报告 + 已归档报告），再弹出年份多选框
    async handleExportCerts() {
      if (!this.selectedRows.length) {
        this.$message.warning('请先在列表中至少选择一条需要导出的记录')
        return
      }
      const ids = this.selectedRows.map(r => r.id)
      try {
        const res = await api.availableYears(ids)
        const years = (res.data && res.data.years) || []
        if (!years.length) {
          this.$message.warning('所选记录均没有可导出（当前或已归档）的报告，暂无法确定导出年份')
          return
        }
        this.exportCertYearOptions = years
        this.exportCertYears = [...years]
        this.exportCertVisible = true
      } catch (e) {
        const msg = e.response?.data?.detail || '获取可导出年份失败，请检查后端服务'
        this.$message.error(msg)
      }
    },
    async confirmExportCerts() {
      if (!this.exportCertYears.length) {
        this.$message.warning('请至少选择一个导出年份')
        return
      }
      this.exporting = true
      try {
        const ids = this.selectedRows.map(r => r.id)
        const res = await api.exportFiles({ ids, years: this.exportCertYears })
        this.saveBlob(res.data, `场所性能报告_导出_${this.formatDateForFile(new Date())}.zip`)
        this.$message.success(`已导出 ${this.exportCertYears.join('/')} 年报告`)
        this.exportCertVisible = false
      } catch (e) {
        let msg = '导出失败，请检查后端服务'
        const resp = e.response
        if (resp && (resp.status === 400 || resp.status === 403)) {
          try {
            const text = await resp.data.text()
            const j = JSON.parse(text)
            if (j.detail) msg = j.detail
          } catch (_) {}
        } else if (resp && resp.status === 401) {
          msg = '未登录或登录已过期，请重新登录'
        }
        this.$message.error(msg)
      } finally {
        this.exporting = false
      }
    },
    saveBlob(buffer, fileName) {
      const blob = (buffer && buffer instanceof Blob) ? buffer : new Blob([buffer])
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    },
    formatDateForFile(d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const h = String(d.getHours()).padStart(2, '0')
      const min = String(d.getMinutes()).padStart(2, '0')
      const s = String(d.getSeconds()).padStart(2, '0')
      return `${y}${m}${day}_${h}${min}${s}`
    },
    // 查看归档记录：拉取本条记录在硬盘「归档」文件夹中的历史报告文件
    async openArchiveHistory(row) {
      this.archiveRow = row
      this.archiveHistoryVisible = true
      this.archiveHistoryLoading = true
      this.archiveFiles = []
      try {
        const res = await api.archiveFiles(row.id)
        this.archiveFiles = (res.data && res.data.results) || []
        this.$set(this.archiveCounts, row.id, this.archiveFiles.length)
      } catch (e) {
        this.$message.error('获取归档记录失败，请检查后端服务')
      } finally {
        this.archiveHistoryLoading = false
      }
    },
    formatSize(n) {
      if (n === null || n === undefined) return '-'
      if (n < 1024) return n + ' B'
      if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB'
      return (n / 1024 / 1024).toFixed(1) + ' MB'
    },
    // 归档图标悬浮提示文案：读缓存的条数；未加载过则提示“查看归档记录”
    archiveTip(row) {
      const c = this.archiveCounts[row.id]
      if (c === undefined) return '查看归档记录'
      if (c === 0) return '暂无归档记录'
      if (c === null) return '加载中…'
      return `${c} 条归档记录`
    },
    // 首次悬浮图标时懒加载该记录的归档条数（之后缓存，避免重复请求）
    onArchiveIconEnter(row) {
      if (this.archiveCounts[row.id] !== undefined) return
      this.$set(this.archiveCounts, row.id, null)
      api.archiveFiles(row.id).then(res => {
        const cnt = (res.data && res.data.count) != null ? res.data.count : 0
        this.$set(this.archiveCounts, row.id, cnt)
      }).catch(() => {
        this.$set(this.archiveCounts, row.id, 0)
      })
    },
    // ---- 删除 ----
    async handleDelete(row) {
      try {
        await this.$confirm(
          `确认删除「${row.device_name}」的场所性能记录吗？删除后其报告文件将移入「删除」文件夹，列表不再显示该记录（数据库保留为已删除状态，可按需恢复）。`,
          '删除确认',
          { type: 'warning' }
        )
      } catch (e) {
        return
      }
      try {
        await api.delete(row.id)
        this.$message.success('删除成功')
        // 跨页保留勾选模式下，清空整个勾选（避免已删行残留在勾选中）
        this.$refs.mainTable.clearSelection()
        this.fetchList()
      } catch (e) {
        this.$message.error('删除失败，请检查后端服务')
      }
    },
    // ---- 归档 ----
    openArchive(row) {
      this.archiveId = row.id
      this.archiveRow = row
      this.archiveForm = {
        department: row.department || '',
        device_name: row.device_name || '',
        factory_no: row.factory_no || '',
        report_time: row.report_time || '',
        expire_date: row.expire_date || '',
        detect_cycle: row.detect_cycle || '',
      }
      this.archiveSiteFile = null
      this.archiveSiteFileList = []
      this.archivePerfFiles = []
      this.archivePerfFileList = []
      this.archiveDialogVisible = true
      this.$nextTick(() => this.calcArchiveExpireDate())
    },
    handleArchiveSiteChange(file, fileList) {
      this.archiveSiteFile = file.raw
      this.archiveSiteFileList = fileList.slice(-1)
    },
    handleArchiveSiteRemove() {
      this.archiveSiteFile = null
      this.archiveSiteFileList = []
    },
    handleArchivePerfChange(file, fileList) {
      this.archivePerfFileList = fileList
      this.archivePerfFiles = fileList.map(f => f.raw)
    },
    handleArchivePerfRemove(file, fileList) {
      this.archivePerfFileList = fileList
      this.archivePerfFiles = fileList.map(f => f.raw)
    },
    async submitArchive() {
      this.$refs.archiveForm.validate(async valid => {
        if (!valid) return
        // 若原记录有文件，归档必须上传对应的新文件，避免静默丢失
        const hadSite = !!this.archiveRow.site_report_path
        const hadPerf = !!(this.archiveRow.performance_reports && this.archiveRow.performance_reports.length)
        if (hadSite && !this.archiveSiteFile) {
          this.$message.warning('该记录原有场所报告，请上传新的场所报告后再归档')
          return
        }
        if (hadPerf && !this.archivePerfFiles.length) {
          this.$message.warning('该记录原有性能报告，请上传新的性能报告后再归档')
          return
        }
        if (!this.archiveSiteFile && !this.archivePerfFiles.length) {
          this.$message.warning('请至少上传一份新的报告文件')
          return
        }
        try {
          const fd = new FormData()
          fd.append('report_time', this.archiveForm.report_time)
          if (this.archiveForm.expire_date) fd.append('expire_date', this.archiveForm.expire_date)
          if (this.archiveForm.detect_cycle !== '' && this.archiveForm.detect_cycle != null) {
            fd.append('detect_cycle', this.archiveForm.detect_cycle)
          }
          if (this.archiveSiteFile) fd.append('site_file', this.archiveSiteFile)
          this.archivePerfFiles.forEach(f => fd.append('performance_file', f))
          await api.archive(this.archiveId, fd)
          this.$message.success('归档成功')
          this.archiveDialogVisible = false
          this.$set(this.archiveCounts, this.archiveId, undefined)
          this.fetchList()
        } catch (e) {
          const data = e.response && e.response.data
          let msg = '归档失败，请检查后端服务'
          if (data) {
            if (data.detail) msg = data.detail
            else if (data.report_time) msg = data.report_time[0]
          }
          this.$message.error(msg)
        }
      })
    },
    resetArchiveForm() {
      this.archiveForm = {
        department: '',
        device_name: '',
        factory_no: '',
        report_time: '',
        expire_date: '',
        detect_cycle: '',
      }
      this.archiveSiteFile = null
      this.archiveSiteFileList = []
      this.archivePerfFiles = []
      this.archivePerfFileList = []
      if (this.$refs.archiveSiteUpload) this.$refs.archiveSiteUpload.clearFiles()
      if (this.$refs.archivePerfUpload) this.$refs.archivePerfUpload.clearFiles()
    },
    resetForm() {
      this.form = this.emptyForm()
      this.siteFile = null
      this.siteFileList = []
      this.perfFiles = []
      this.perfFileList = []
      this.backfilledKeys = []
      this.ocrFilled = false
      this.ocrConfirmed = false
      if (this.$refs.siteUpload) this.$refs.siteUpload.clearFiles()
      if (this.$refs.perfUpload) this.$refs.perfUpload.clearFiles()
    },
  },
}
</script>

<style scoped>
.sp-list {
  padding: 16px;
}
.search-card /deep/ .el-card__body {
  padding: 0;
}
.search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.search-title {
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  user-select: none;
}
.search-title:hover {
  color: #409eff;
}
/* 查询区缩小一号：label 13px，间距收紧（控件尺寸由 size="small" 接管） */
.search-form /deep/ .el-form-item__label {
  font-size: 13px;
}
.search-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 10px 12px;
}
.search-form .el-form-item {
  margin-bottom: 8px;
}
.search-buttons {
  margin-left: auto;
}
.table-card {
  margin-top: 16px;
}
.table-toolbar {
  margin-bottom: 12px;
  text-align: left;
}
/* 导出资质年份选择弹窗 */
.export-year-tip {
  color: #606266;
  font-size: 14px;
  margin-bottom: 14px;
  line-height: 1.6;
}
.export-year-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-left: 6px;
}
.export-year-group .el-checkbox {
  margin-right: 0;
}
.sp-table /deep/ .el-table__body tr:hover > td {
  background-color: #ecf5ff !important;
}
/* 到期高亮（与报告提醒/计量提醒统一配色，能在暖米白底上分辨） */
.sp-table /deep/ .el-table__body tr.expired-row > td {
  background-color: #fad7d5 !important;
}
.sp-table /deep/ .el-table__body tr.expired-row > td:first-child {
  border-left: 3px solid #f56c6c;
}
.sp-table /deep/ .el-table__body tr.soon-row > td {
  background-color: #fbe9c9 !important;
}
.sp-table /deep/ .el-table__body tr.soon-row > td:first-child {
  border-left: 3px solid #e6a23c;
}
.sp-table /deep/ .el-table__body tr.expired-row:hover > td {
  background-color: #f6c6c3 !important;
}
.sp-table /deep/ .el-table__body tr.soon-row:hover > td {
  background-color: #f7dfb2 !important;
}
.sp-table /deep/ .el-table__body tr > td {
  transition: background-color 0.2s;
}
.sp-table /deep/ th,
.sp-table /deep/ td {
  text-align: center !important;
}
.sp-table /deep/ th .cell {
  text-align: center !important;
}
.sp-table /deep/ td .cell {
  text-align: center !important;
}
.pager {
  margin-top: 16px;
  text-align: right;
}
.pdf-link {
  color: #409eff;
  margin: 0 2px;
}
.pdf-none {
  color: #c0c4cc;
}
.archive-text {
  color: #e6a23c;
}
.danger-text {
  color: #f56c6c;
}
.sp-form /deep/ .el-input__inner {
  border-color: #409eff;
  font-size: 15px;
}
.sp-form /deep/ .el-textarea__inner {
  border-color: #409eff;
  font-size: 15px;
}
.ocr-confirm-check {
  margin-right: 10px;
}
.sp-form /deep/ .ocr-filled .el-input__inner,
.sp-form /deep/ .ocr-filled .el-textarea__inner {
  background: #fdf6ec;
}
.pdf-uploader {
  width: 100%;
}
.pdf-existing {
  margin-bottom: 8px;
}
</style>
